export const isAnswered = (question, value) => {
    if (question.type === 'checkbox') return value === true;
    if (question.type === 'signature') return Boolean(value?.signed);
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'string') return value.trim().length > 0;
    return Boolean(value);
};

// Builds the `answers` payload for a submit. Locked questions map to the dedicated FormData
// fields instead, and 'info' questions are never answerable.
//
// An entry is omitted only when its value is undefined or null. That matters in both
// directions: JSON.stringify drops undefined, so an entry for an untouched question would
// reach the backend with no `answer` key at all and be rejected as "must be a string" —
// failing the whole submission over a blank optional field. But an explicitly emptied value
// ('', [], false, an unsigned signature) must still be sent, because answer rows are upserted
// and never deleted, so omitting one leaves the old value stored.
//
// Two types cannot express "cleared": date needs a number and yesno needs 1 or 2. Clearing
// works for the text family, checklist, checkbox and signature.
export const collectAnswers = (sections, values) => {
    const answers = [];
    sections.forEach(section => {
        section.questions.forEach(question => {
            if (!question.enabled || question.locked || question.type === 'info') return;
            const value = values[question.questionKey];
            if (value === undefined || value === null) return;
            answers.push({ questionId: question.questionKey, answer: value });
        });
    });
    return answers;
};

export const getSectionProgress = (section, values) => {
    const requiredQuestions = section.questions.filter(question => question.enabled && question.required && question.type !== 'info');
    const completed = requiredQuestions.filter(question => isAnswered(question, values[question.questionKey])).length;
    return {
        required: requiredQuestions.length,
        completed,
        // A section with nothing required is complete on arrival — there is genuinely nothing
        // to do in it. Reading that as incomplete instead leaves an all-optional section stuck
        // showing its step number forever, however much the applicant fills in; since the
        // director can now make every section but Applicant Details optional, that would be
        // most of the form.
        isComplete: requiredQuestions.length === 0 || completed === requiredQuestions.length,
    };
};

export const getOverallProgress = (sections, values) => {
    return sections.reduce(
        (total, section) => {
            const progress = getSectionProgress(section, values);
            return { required: total.required + progress.required, completed: total.completed + progress.completed };
        },
        { required: 0, completed: 0 }
    );
};
